import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Activity, Gauge, Inbox, Clock, Target, TrendingUp } from 'lucide-react';
import { format, differenceInHours } from 'date-fns';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { calculateOBLI, formatOBLIScore, getOBLIColorClasses } from '@/utils/obliCalculation';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { getShopDayLabel, getTodayInShopTz } from '@/utils/shopTime';
import { useOBLIShopDay } from '@/hooks/kpi/useOBLIShopDay';
// DTC-K: route shop-health "now" anchors (parking-dwell aging + 30d
// promise-window) through the canonical clock so analytics + health
// scoring are deterministic under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
// DMC-D3c-1: canonical deleted-state helper.
import { isTicketDeleted } from '@/utils/ticketDeletedState';

interface ShopHealthDashboardProps {
  shopId: string;
  selectedDate: Date;
  technicians: any[];
  tickets: any[];
}

export function ShopHealthDashboard({ shopId, selectedDate, technicians, tickets }: ShopHealthDashboardProps) {
  const { shopTimezone } = useShopTimezone();
  const dateISO = getTodayInShopTz(shopTimezone);

  // Filter tickets for the selected date (exclude parking lot tickets from workload/OBLI)
  const dayTickets = tickets.filter(t => {
    if (t.parking_lot) return false; // Parking lot tickets don't count toward workload
    // R9-H-2: scheduled_date first; fallback via shop TZ, never UTC split
    const ticketDate = t.scheduled_date ??
      (t.start_time ? getShopDayLabel(t.start_time, shopTimezone) : null);
    // DMC-D3c-1: canonical deleted-state check (both reps).
    return ticketDate === dateISO && !isTicketDeleted(t) && t.status !== 'Completed';
  });

  // Core-100 R4 wave 2 (CORE-AV D): the load index here is assembled by the SAME assembler as the Dayboard and the
  // calendar (assembleOBLIDay, via useOBLIShopDay) — same day tickets, same mechanical roster, same capacity
  // authority, staffing stress in MINUTES, real shop hours and holidays (no "assume open"). It used to come from two
  // analytics RPCs (K-T-2 kpi_shop_capacity / K-T-3 kpi_shop_load_inputs) with their own capacity model and a
  // head-count staffing ratio, so one shop-day could read differently here than on the board.
  const { day: obliDay } = useOBLIShopDay(shopId, dateISO, shopTimezone);
  const totalCapacity = obliDay?.totalCapacity ?? 0;
  const bookedMinutes = obliDay?.input.bookedMinutes ?? 0;
  // Not assembled yet: the index's own "Capacity unavailable" state, never a fabricated 0 or 100.
  const obliResult = calculateOBLI(obliDay?.input ?? {
    capacityKnown: false, totalCapacity: 0, bookedMinutes: 0, ticketCount: 0, waiterCount: 0,
    specialtyJobCount: 0, workingTechCount: 0, totalTechCount: 0, isShopOpen: true,
  });

  const obliScore = obliResult.score;
  const obliStyle = {
    ...getOBLIColorClasses(obliScore),
    label: obliResult.label
  };

  // Calculate backlog (unassigned + parking lot)
  const unassignedCount = dayTickets.filter(t => !t.assigned_tech_id && !t.parking_lot).length;
  // DMC-D3c-1: canonical deleted-state check (both reps).
  const parkingLotTickets = tickets.filter(t => t.parking_lot === true && !isTicketDeleted(t) && t.status !== 'Completed');
  const parkingLotCount = parkingLotTickets.length;
  const backlogVolume = unassignedCount + parkingLotCount;

  // Calculate parking lot dwell (average age in hours)
  const avgParkingLotDwell = parkingLotTickets.length > 0
    ? parkingLotTickets.reduce((sum, t) => {
        const createdAt = new Date(t.created_at);
        return sum + differenceInHours(clock.now(), createdAt);
      }, 0) / parkingLotTickets.length
    : 0;

  // Fetch shop-level waiter promise health
  const { data: promiseHealth } = useQuery({
    queryKey: ['shop-promise-health', shopId, dateISO],
    queryFn: async () => {
      const thirtyDaysAgo = clock.now();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('waiter_promise_tracking')
        .select('on_time')
        .eq('shop_id', shopId)
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (error) throw error;

      const total = data?.length || 0;
      const onTime = data?.filter(d => d.on_time === true).length || 0;
      const accuracy = total > 0 ? (onTime / total) * 100 : null;

      return { total, onTime, accuracy };
    }
  });

  const dailyUtilization = totalCapacity > 0 ? (bookedMinutes / totalCapacity) * 100 : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-muted-foreground" />
          Shop Health Dashboard
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* OBLI Score */}
        <div className={cn('p-4 rounded-xl', obliStyle.bg)}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Gauge className="h-5 w-5" />
              <span className="font-medium">OBLI Score</span>
            </div>
            <span className={cn('text-2xl font-bold', obliStyle.text)} data-testid="shop-health-obli-score" data-obli-state={obliResult.state}>{formatOBLIScore(obliResult)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">{obliStyle.label}</span>
            <span className="text-sm text-muted-foreground">
              {bookedMinutes} / {totalCapacity} min capacity
            </span>
          </div>
          <Progress value={obliScore} className="mt-2 h-2" />
        </div>

        {/* Grid of metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Daily Utilization */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-primary" />
              <p className="text-xs text-muted-foreground">Daily Utilization</p>
            </div>
            <p className={cn(
              'font-mono text-xl font-bold',
              dailyUtilization > 100 ? 'text-destructive' : 
              dailyUtilization > 85 ? 'text-warning' : 'text-success'
            )}>
              {dailyUtilization.toFixed(0)}%
            </p>
          </div>

          {/* Backlog Volume */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Inbox className="h-4 w-4 text-warning" />
              <p className="text-xs text-muted-foreground">Backlog</p>
            </div>
            <p className={cn(
              'font-mono text-xl font-bold',
              backlogVolume > 5 ? 'text-destructive' : 
              backlogVolume > 2 ? 'text-warning' : 'text-foreground'
            )}>
              {backlogVolume}
            </p>
            <p className="text-xs text-muted-foreground">
              {unassignedCount} unassigned + {parkingLotCount} parking
            </p>
          </div>

          {/* Parking Lot Dwell */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Clock className="h-4 w-4 text-accent" />
              <p className="text-xs text-muted-foreground">Parking Dwell</p>
            </div>
            <p className={cn(
              'font-mono text-xl font-bold',
              avgParkingLotDwell > 24 ? 'text-destructive' : 
              avgParkingLotDwell > 8 ? 'text-warning' : 'text-foreground'
            )}>
              {avgParkingLotDwell > 0 ? `${avgParkingLotDwell.toFixed(0)}h` : 'N/A'}
            </p>
            <p className="text-xs text-muted-foreground">avg ticket age</p>
          </div>

          {/* Waiter Promise Health */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Target className="h-4 w-4 text-success" />
              <p className="text-xs text-muted-foreground">Promise Health</p>
            </div>
            <p className={cn(
              'font-mono text-xl font-bold',
              promiseHealth?.accuracy !== null && promiseHealth?.accuracy !== undefined
                ? promiseHealth.accuracy >= 85 
                  ? 'text-success' 
                  : promiseHealth.accuracy >= 70 
                    ? 'text-warning' 
                    : 'text-destructive'
                : 'text-muted-foreground'
            )}>
              {promiseHealth?.accuracy !== null && promiseHealth?.accuracy !== undefined 
                ? `${promiseHealth.accuracy.toFixed(0)}%` 
                : 'N/A'}
            </p>
            <p className="text-xs text-muted-foreground">
              {promiseHealth?.total || 0} waiters (30d)
            </p>
          </div>
        </div>

        {/* Summary bar */}
        <div className="p-3 bg-muted/30 rounded-lg">
          <p className="text-sm text-muted-foreground">
            {obliResult.description}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}